import React, { useEffect, useState } from 'react'
import { EmailFrame } from './EmailPreview.styles'

const MIN_HEIGHT = 480
const EARLY_POLL_MS = 200
const EARLY_POLL_WINDOW_MS = 3000

interface SendGridEmailFrameProps {
  html: string
  title: string
}

/**
 * Shows rendered email HTML in an isolated frame, so its inline styles look
 * exactly as in a mail client and cannot leak into the app. Scripts stay off;
 * same-origin is allowed only so the frame can size itself to the email. A
 * short email shows whole; a long one scrolls inside the capped frame.
 */
const SendGridEmailFrame: React.FC<SendGridEmailFrameProps> = ({
  html,
  title,
}) => {
  const [frame, setFrame] = useState<HTMLIFrameElement | null>(null)
  const [height, setHeight] = useState(MIN_HEIGHT)

  // The frame's content can settle after "load" (late images, an early load
  // event for srcDoc), so keep following its size instead of measuring once.
  // The frame starts on a blank document and swaps to the email on load, so
  // the observer follows whichever body is current rather than the first one.
  useEffect(() => {
    if (!frame) return undefined
    const observer = new ResizeObserver(() => measure())
    let observedBody: HTMLElement | null = null
    function measure() {
      const doc = frame?.contentDocument
      if (!doc?.body) return
      if (doc.body !== observedBody) {
        if (observedBody) observer.unobserve(observedBody)
        observedBody = doc.body
        observer.observe(doc.body)
      }
      setHeight(Math.max(MIN_HEIGHT, doc.body.scrollHeight))
    }
    frame.addEventListener('load', measure)
    measure()
    // "load" waits for the email's remote images, so also check briefly while
    // the document is first being parsed.
    const poll = window.setInterval(measure, EARLY_POLL_MS)
    const stopPoll = window.setTimeout(
      () => window.clearInterval(poll),
      EARLY_POLL_WINDOW_MS
    )
    return () => {
      frame.removeEventListener('load', measure)
      observer.disconnect()
      window.clearInterval(poll)
      window.clearTimeout(stopPoll)
    }
  }, [frame, html])

  return (
    <EmailFrame
      innerRef={setFrame}
      title={title}
      srcDoc={html}
      sandbox="allow-same-origin"
      style={{ height }}
    />
  )
}

export { SendGridEmailFrame }
